import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import {
  FiUser, FiLock, FiLogIn, FiShield, FiDownload,
  FiCheckCircle, FiActivity,
  FiSmartphone, FiChevronDown,
  FiZap, FiCamera, FiArrowRight,
  FiTrendingUp, FiLayers,
} from "react-icons/fi";
import logo from "../assets/images/achal_logo.png";

// ─── Inject global CSS (once) ──────────────────────────────────────────────────
const globalCSS = `
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(6px); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; overflow-x: hidden; }

  /* ─── Scrollable page wrapper ─── */
  .achal-page {
    min-height: 100vh;
    background: #F8FAFC;
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  /* ─── Hero section ─── */
  .achal-hero {
    position: relative;
    padding: 60px 5%;
    background: #F8FAFC;
  }
  .achal-hero-bg {
    position: absolute;
    top: 0; right: 0;
    width: 50%; height: 100%;
    background: radial-gradient(circle at 100% 0%, rgba(184,150,46,0.08) 0%, transparent 70%);
    pointer-events: none;
  }

  /* ─── Hero inner grid ─── */
  .achal-hero-grid {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 0.9fr;
    gap: 60px;
    align-items: start;
  }

  /* ─── Left panel ─── */
  .achal-left { padding: 20px 20px 20px 0; }

  .achal-brand-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 56px;
  }
  .achal-logo-wrap {
    background: linear-gradient(135deg, #B8962E20, #D4AF3720);
    padding: 12px;
    border-radius: 20px;
    flex-shrink: 0;
  }
  .achal-logo-wrap img { width: 70px; height: auto; display: block; }
  .achal-est  { font-size: 10px; font-weight: 700; color: #B8962E; letter-spacing: 1px; margin-bottom: 4px; }
  .achal-name { margin: 0; font-size: 20px; font-weight: 800; color: #0F172A; letter-spacing: 1px; }
  .achal-tag  { margin: 0; font-size: 9px; color: #94A3B8; font-weight: 600; letter-spacing: 0.5px; }

  .achal-eyebrow { font-size: 14px; font-weight: 700; color: #B8962E; text-transform: uppercase; letter-spacing: 3px; margin-bottom: 16px; }
  .achal-h1 { font-size: 68px; font-weight: 800; color: #0F172A; line-height: 1.1; margin: 0 0 24px; }
  .achal-h1 span { color: #B8962E; }
  .achal-sub { font-size: 17px; color: #64748B; line-height: 1.6; max-width: 500px; margin-bottom: 40px; }

  /* Stats strip */
  .achal-stats {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    background: #fff;
    padding: 20px 24px;
    border-radius: 24px;
    margin-bottom: 36px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.03);
    border: 1px solid #F1F5F9;
  }
  .achal-stat { display: flex; align-items: center; gap: 12px; }
  .achal-stat-icon {
    width: 40px; height: 40px; background: #F8FAFC;
    border-radius: 12px; display: flex; align-items: center;
    justify-content: center; color: #B8962E; flex-shrink: 0;
  }
  .achal-stat h4 { margin: 0; font-size: 17px; font-weight: 800; color: #0F172A; }
  .achal-stat p  { margin: 0; font-size: 11px; color: #94A3B8; font-weight: 500; }
  .achal-divider { width: 1px; height: 30px; background: #E2E8F0; flex-shrink: 0; }

  /* APK button */
  .achal-apk-btn {
    display: inline-flex; align-items: center; gap: 12px;
    background: linear-gradient(90deg, #0F172A, #1E293B);
    color: #fff; padding: 15px 26px; border-radius: 40px;
    border: none; font-weight: 700; font-size: 14px; cursor: pointer;
    box-shadow: 0 8px 20px rgba(0,0,0,0.12); margin-bottom: 44px;
    max-width: 100%;
  }

  .achal-scroll-hint {
    display: flex; align-items: center; gap: 12px;
    font-size: 12px; font-weight: 600; color: #94A3B8; letter-spacing: 1.5px;
  }
  .achal-scroll-dot { width: 6px; height: 6px; background: #B8962E; border-radius: 50%; flex-shrink: 0; }

  /* ─── Right panel / Login card ─── */
  .achal-right { display: flex; justify-content: center; }

  .achal-card {
    width: 100%; max-width: 440px;
    background: #fff; border-radius: 36px;
    box-shadow: 0 40px 60px -20px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.02);
    overflow: hidden;
  }
  .achal-card-top { height: 4px; background: linear-gradient(90deg, #B8962E, #D4AF37, #B8962E); }
  .achal-card-head { padding: 36px 40px 0; text-align: center; }
  .achal-lock-wrap {
    width: 56px; height: 56px;
    background: linear-gradient(135deg, #B8962E10, #D4AF3710);
    border-radius: 28px; display: flex; align-items: center;
    justify-content: center; margin: 0 auto 20px; color: #B8962E;
  }
  .achal-card-title    { font-size: 26px; font-weight: 800; color: #0F172A; margin-bottom: 8px; }
  .achal-card-subtitle { font-size: 14px; color: #94A3B8; margin-bottom: 0; }

  .achal-form { padding: 28px 40px 36px; }
  .achal-field { margin-bottom: 22px; }
  .achal-label { font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 8px; display: block; }
  .achal-input-wrap {
    position: relative; border-radius: 16px;
    border: 2px solid #E2E8F0; background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .achal-input-wrap.focused {
    border-color: #B8962E;
    box-shadow: 0 0 0 4px rgba(184,150,46,0.1);
  }
  .achal-input-icon {
    position: absolute; left: 16px; top: 50%;
    transform: translateY(-50%); color: #94A3B8;
  }
  .achal-input {
    width: 100%; padding: 16px 16px 16px 48px;
    border: none; outline: none; border-radius: 16px;
    font-size: 15px; background: transparent;
  }
  .achal-submit {
    width: 100%; padding: 16px;
    background: linear-gradient(90deg, #B8962E, #D4AF37);
    border: none; border-radius: 16px;
    color: #0F172A; font-weight: 800; font-size: 15px;
    cursor: pointer; display: flex; align-items: center;
    justify-content: center; gap: 10px; margin-top: 6px;
  }
  .achal-spinner {
    width: 20px; height: 20px;
    border: 2px solid #0F172A; border-top-color: transparent;
    border-radius: 50%; animation: spin 0.8s linear infinite;
    display: inline-block;
  }
  .achal-sec-bar {
    padding: 14px 40px 28px; border-top: 1px solid #F1F5F9;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .achal-sec-bar p { font-size: 11px; color: #94A3B8; margin: 0; }

  /* ─── Workflow section ─── */
  .achal-workflow { padding: 80px 5%; background: #fff; }
  .achal-section-head { text-align: center; margin-bottom: 56px; }
  .achal-badge {
    display: inline-block; font-size: 12px; font-weight: 700;
    color: #B8962E; text-transform: uppercase; letter-spacing: 2px;
    background: #B8962E10; padding: 6px 16px; border-radius: 40px; margin-bottom: 18px;
  }
  .achal-section-h2 { font-size: 40px; font-weight: 800; color: #0F172A; margin-bottom: 14px; }
  .achal-section-h2 span { color: #B8962E; }
  .achal-section-p { font-size: 16px; color: #64748B; max-width: 600px; margin: 0 auto; }

  .achal-flow-row {
    display: flex; flex-wrap: wrap; justify-content: center;
    align-items: center; gap: 14px; max-width: 1200px; margin: 0 auto;
  }
  .achal-flow-arrow { color: #CBD5E1; flex-shrink: 0; }
  .achal-flow-card {
    flex: 1; min-width: 170px; background: #F8FAFC;
    padding: 30px 22px; border-radius: 26px; text-align: center; position: relative;
  }
  .achal-flow-num {
    position: absolute; top: 14px; left: 18px;
    font-size: 40px; font-weight: 800; color: #E2E8F0; line-height: 1;
  }
  .achal-flow-icon { color: #B8962E; margin-bottom: 18px; }
  .achal-flow-h3 { font-size: 17px; font-weight: 700; color: #0F172A; margin-bottom: 10px; }
  .achal-flow-p  { font-size: 13px; color: #64748B; line-height: 1.5; margin: 0; }

  /* ─── Modules section ─── */
  .achal-modules { padding: 80px 5%; background: #F8FAFC; }
  .achal-module-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 28px; max-width: 1200px; margin: 0 auto;
  }
  .achal-module-card {
    background: #fff; border-radius: 30px; padding: 30px;
    border: 1px solid #F1F5F9;
  }
  .achal-mod-icon { color: #B8962E; margin-bottom: 18px; }
  .achal-mod-head { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 22px; }
  .achal-mod-title { font-size: 19px; font-weight: 800; color: #0F172A; margin: 0; }
  .achal-mod-tag {
    font-size: 10px; font-weight: 700; color: #B8962E;
    background: #B8962E10; padding: 4px 12px; border-radius: 40px;
  }
  .achal-mod-list { list-style: none; padding: 0; margin: 0; }
  .achal-mod-list li {
    display: flex; align-items: flex-start; gap: 10px;
    font-size: 14px; color: #475569; padding: 9px 0;
    border-bottom: 1px solid #F8FAFC; line-height: 1.4;
  }
  .achal-mod-list li:last-child { border-bottom: none; }
  .achal-mod-list li svg { color: #B8962E; flex-shrink: 0; margin-top: 2px; }

  /* ─── Download section ─── */
  .achal-download { padding: 80px 5%; background: #0F172A; }
  .achal-dl-grid {
    max-width: 1200px; margin: 0 auto;
    display: grid; grid-template-columns: 1fr 0.8fr;
    gap: 60px; align-items: center;
  }
  .achal-dl-badge {
    display: inline-block; font-size: 12px; font-weight: 700;
    color: #B8962E; text-transform: uppercase; letter-spacing: 2px;
    background: rgba(184,150,46,0.15); padding: 6px 16px; border-radius: 40px; margin-bottom: 22px;
  }
  .achal-dl-h2 { font-size: 40px; font-weight: 800; color: #fff; margin-bottom: 18px; line-height: 1.2; }
  .achal-dl-h2 span { color: #B8962E; }
  .achal-dl-p { font-size: 16px; color: #94A3B8; line-height: 1.6; margin-bottom: 30px; }
  .achal-dl-btn {
    display: inline-flex; align-items: center; gap: 12px;
    background: linear-gradient(90deg, #B8962E, #D4AF37);
    padding: 15px 30px; border-radius: 40px; border: none;
    font-weight: 800; font-size: 15px; color: #0F172A;
    cursor: pointer; margin-bottom: 26px;
  }
  .achal-dl-feats { display: flex; gap: 20px; flex-wrap: wrap; font-size: 13px; color: #CBD5E1; }

  .achal-mockup-wrap { display: flex; justify-content: center; }
  .achal-mockup {
    width: 220px; height: 420px; background: #1E293B;
    border-radius: 30px; padding: 10px;
    box-shadow: 0 40px 60px rgba(0,0,0,0.3);
  }
  .achal-mockup-screen {
    width: 100%; height: 100%; background: #F8FAFC;
    border-radius: 22px; overflow: hidden;
    display: flex; flex-direction: column;
  }
  .achal-mockup-hdr {
    display: flex; justify-content: space-between;
    padding: 10px 14px; background: #fff; border-bottom: 1px solid #F1F5F9;
  }
  .achal-mockup-time { font-size: 11px; font-weight: 600; }
  .achal-mockup-bat  { width: 18px; height: 9px; border: 1px solid #CBD5E1; border-radius: 2px; }
  .achal-mockup-body {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 10px; padding: 20px;
  }
  .achal-mockup-emoji { font-size: 44px; }
  .achal-mockup-label { font-size: 13px; font-weight: 700; color: #0F172A; }
  .achal-mockup-cam {
    width: 66px; height: 66px; background: #B8962E10;
    border-radius: 33px; display: flex; align-items: center;
    justify-content: center; color: #B8962E;
  }

  /* ════════════════════════════════════════════
     TABLET  ≤ 1024px
  ════════════════════════════════════════════ */
  @media (max-width: 1024px) {
    .achal-hero-grid {
      grid-template-columns: 1fr;
      gap: 48px;
    }
    .achal-left { padding: 0; }
    .achal-h1 { font-size: 52px; }
    .achal-dl-grid { grid-template-columns: 1fr; }
    .achal-mockup-wrap { display: none; }
  }

  /* ════════════════════════════════════════════
     MOBILE  ≤ 640px
  ════════════════════════════════════════════ */
  @media (max-width: 640px) {
    .achal-hero { padding: 36px 20px 48px; }
    .achal-hero-bg { display: none; }

    .achal-brand-row { margin-bottom: 32px; }
    .achal-logo-wrap img { width: 54px; }
    .achal-name { font-size: 17px; }

    .achal-eyebrow { font-size: 11px; letter-spacing: 2px; }
    .achal-h1 { font-size: 36px; }
    .achal-sub { font-size: 15px; margin-bottom: 28px; }

    .achal-stats { flex-direction: column; align-items: flex-start; padding: 16px 18px; margin-bottom: 28px; }
    .achal-divider { width: 100%; height: 1px; }

    .achal-apk-btn {
      width: 100%; justify-content: center;
      font-size: 13px; padding: 14px 16px; margin-bottom: 32px;
    }
    .achal-scroll-hint { display: none; }

    /* Login card full-width on mobile */
    .achal-right { display: block; }
    .achal-card { max-width: 100%; border-radius: 28px; }
    .achal-card-head { padding: 28px 22px 0; }
    .achal-form { padding: 22px 22px 28px; }
    .achal-sec-bar { padding: 12px 22px 24px; }
    .achal-card-title { font-size: 22px; }

    .achal-workflow { padding: 56px 20px; }
    .achal-modules  { padding: 56px 20px; }
    .achal-download { padding: 56px 20px; }

    .achal-section-h2 { font-size: 26px; }
    .achal-section-p  { font-size: 14px; }

    .achal-flow-arrow { display: none; }
    .achal-flow-card  { min-width: 100%; }

    .achal-dl-h2 { font-size: 26px; }
    .achal-dl-p  { font-size: 14px; }
    .achal-dl-btn { width: 100%; justify-content: center; }
    .achal-dl-feats { gap: 12px; font-size: 12px; }
  }

  /* ════════════════════════════════════════════
     VERY SMALL  ≤ 380px
  ════════════════════════════════════════════ */
  @media (max-width: 380px) {
    .achal-h1 { font-size: 28px; }
    .achal-logo-wrap img { width: 46px; }
    .achal-name { font-size: 14px; }
  }
`;

if (!document.getElementById("achal-css")) {
  const tag = document.createElement("style");
  tag.id = "achal-css";
  tag.textContent = globalCSS;
  document.head.appendChild(tag);
}

// ─────────────────────────────────────────────────────────────────────────────

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [focused, setFocused]   = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role  = localStorage.getItem("role");
    if (token) {
      if (role === "ADMIN") navigate("/admin/dashboard");
      else if (role === "HOD")  navigate("/dashboard");
      else if (role === "USER") navigate("/user/dashboard");
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await API.post("/auth/login", { username, password });
      if (res.data.success) {
        const { token, user } = res.data;
        localStorage.setItem("token",     token);
        localStorage.setItem("role",      user.role);
        localStorage.setItem("user_id",   user.id);
        localStorage.setItem("full_name", user.full_name);
        if (user.role === "ADMIN") navigate("/admin/dashboard");
        else if (user.role === "HOD")  navigate("/dashboard");
        else if (user.role === "USER") navigate("/user/dashboard");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Authentication Failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadAPK = () =>
    window.open("https://ajplservices.tech/downloads/achal-support.apk", "_blank");

  return (
    <div className="achal-page">

      {/* ── HERO ── */}
      <section className="achal-hero">
        <div className="achal-hero-bg" />
        <div className="achal-hero-grid">

          {/* LEFT */}
          <div className="achal-left">
            <div className="achal-brand-row">
              <div className="achal-logo-wrap">
                <img src={logo} alt="Achal Jewels" />
              </div>
              <div>
                <div className="achal-est">EST. 1985</div>
                <h2 className="achal-name">ACHAL JEWELS</h2>
                <p className="achal-tag">PRECISION INFRASTRUCTURE</p>
              </div>
            </div>

            <div className="achal-eyebrow">Welcome Back</div>
            <h1 className="achal-h1">
              Universal<br /><span>Support Portal</span>
            </h1>
            <p className="achal-sub">
              Centralized incident management ecosystem for seamless maintenance tracking and resolution.
            </p>

            <div className="achal-stats">
              <div className="achal-stat">
                <div className="achal-stat-icon"><FiTrendingUp /></div>
                <div><h4>24/7</h4><p>System Uptime</p></div>
              </div>
              <div className="achal-divider" />
              <div className="achal-stat">
                <div className="achal-stat-icon"><FiActivity /></div>
                <div><h4>&lt;15m</h4><p>Avg Response</p></div>
              </div>
              <div className="achal-divider" />
              <div className="achal-stat">
                <div className="achal-stat-icon"><FiShield /></div>
                <div><h4>100%</h4><p>Secure Access</p></div>
              </div>
            </div>

            <button className="achal-apk-btn" onClick={downloadAPK}>
              <FiDownload size={20} />
              <span>DOWNLOAD ANDROID APP (APK)</span>
              <FiArrowRight size={16} />
            </button>

            <div className="achal-scroll-hint">
              <span className="achal-scroll-dot" />
              EXPLORE THE ECOSYSTEM
              <FiChevronDown style={{ animation: "bounce 2s infinite" }} />
            </div>
          </div>

          {/* RIGHT — Login card */}
          <div className="achal-right">
            <div className="achal-card">
              <div className="achal-card-top" />
              <div className="achal-card-head">
                <div className="achal-lock-wrap"><FiLock size={24} /></div>
                <h3 className="achal-card-title">Staff Access</h3>
                <p className="achal-card-subtitle">Enter your credentials to continue</p>
              </div>

              <form className="achal-form" onSubmit={handleLogin}>
                <div className="achal-field">
                  <label className="achal-label">Username / Terminal ID</label>
                  <div className={`achal-input-wrap${focused === "u" ? " focused" : ""}`}>
                    <FiUser className="achal-input-icon" />
                    <input
                      className="achal-input"
                      type="text"
                      placeholder="Enter your username"
                      onChange={e => setUsername(e.target.value)}
                      onFocus={() => setFocused("u")}
                      onBlur={() => setFocused(null)}
                      required
                    />
                  </div>
                </div>
                <div className="achal-field">
                  <label className="achal-label">Access Key / Password</label>
                  <div className={`achal-input-wrap${focused === "p" ? " focused" : ""}`}>
                    <FiLock className="achal-input-icon" />
                    <input
                      className="achal-input"
                      type="password"
                      placeholder="Enter your password"
                      onChange={e => setPassword(e.target.value)}
                      onFocus={() => setFocused("p")}
                      onBlur={() => setFocused(null)}
                      required
                    />
                  </div>
                </div>
                <button type="submit" disabled={loading} className="achal-submit">
                  {loading ? (
                    <span className="achal-spinner" />
                  ) : (
                    <><FiLogIn size={18} /><span>ENTER SYSTEM</span><FiArrowRight size={16} /></>
                  )}
                </button>
              </form>

              <div className="achal-sec-bar">
                <FiShield size={12} color="#94A3B8" />
                <p>Authorized access only. All activities are logged securely.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── WORKFLOW ── */}
      <section className="achal-workflow">
        <div className="achal-section-head">
          <div className="achal-badge">HOW IT WORKS</div>
          <h2 className="achal-section-h2">
            Intelligent <span>Ticket Resolution</span> Flow
          </h2>
          <p className="achal-section-p">From issue identification to resolution in four simple steps</p>
        </div>
        <div className="achal-flow-row">
          {[
            { n: "01", Icon: FiSmartphone,  title: "Scan & Identify",  desc: "Scan unique QR Code at machine/location using the Mobile App" },
            { n: "02", Icon: FiCamera,      title: "Attach Evidence",  desc: "Capture photos, videos, or voice notes with detailed description" },
            { n: "03", Icon: FiZap,         title: "Instant Dispatch", desc: "Auto-routing to respective department HOD in real-time" },
            { n: "04", Icon: FiCheckCircle, title: "Resolution",       desc: "HOD assigns worker, ticket closed with performance report" },
          ].map(({ n, Icon, title, desc }, i, arr) => (
            <React.Fragment key={n}>
              <div className="achal-flow-card">
                <div className="achal-flow-num">{n}</div>
                <div className="achal-flow-icon"><Icon size={30} /></div>
                <h3 className="achal-flow-h3">{title}</h3>
                <p className="achal-flow-p">{desc}</p>
              </div>
              {i < arr.length - 1 && (
                <div className="achal-flow-arrow"><FiArrowRight size={22} /></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── MODULES ── */}
      <section className="achal-modules">
        <div className="achal-section-head">
          <div className="achal-badge">PORTAL MODULES</div>
          <h2 className="achal-section-h2">
            Comprehensive <span>Dashboard</span> Ecosystem
          </h2>
          <p className="achal-section-p">Role-based access with powerful features for every stakeholder</p>
        </div>
        <div className="achal-module-grid">
          {[
            {
              Icon: FiShield, title: "Admin Module", tag: "Super Admin",
              items: ["Manage Categories & Sub-categories", "Add/Remove HODs and Staff Users", "Live Monitoring of all tickets", "Generate QR Codes for new locations"],
            },
            {
              Icon: FiLayers, title: "HOD Module", tag: "Department Head",
              items: ["Receive instant alerts for department issues", "Assign specialized workers to tickets", "Track time taken for each resolution", "Review feedback and machine downtime"],
            },
            {
              Icon: FiUser, title: "User Terminal", tag: "Floor Staff",
              items: ["Raise issues via Terminal or Mobile App", "Upload media evidence for faster fixes", "Check status of previously raised tickets", "User-friendly interface for floor artisans"],
            },
          ].map(({ Icon, title, tag, items }) => (
            <div key={title} className="achal-module-card">
              <div className="achal-mod-icon"><Icon size={28} /></div>
              <div className="achal-mod-head">
                <h3 className="achal-mod-title">{title}</h3>
                <span className="achal-mod-tag">{tag}</span>
              </div>
              <ul className="achal-mod-list">
                {items.map(item => (
                  <li key={item}><FiCheckCircle size={14} />{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── DOWNLOAD ── */}
      <section className="achal-download">
        <div className="achal-dl-grid">
          <div>
            <div className="achal-dl-badge">MOBILE ACCESS</div>
            <h2 className="achal-dl-h2">Take Control <span>On The Go</span></h2>
            <p className="achal-dl-p">
              Download the official Achal Jewels Support App for Android devices. Scan QR codes, raise tickets, and track resolutions from anywhere on the factory floor.
            </p>
            <button className="achal-dl-btn" onClick={downloadAPK}>
              <FiDownload size={20} /><span>Download APK Now</span><FiArrowRight size={16} />
            </button>
            <div className="achal-dl-feats">
              <span>✓ QR Code Scanner</span>
              <span>✓ Offline Support</span>
              <span>✓ Real-time Updates</span>
            </div>
          </div>

          <div className="achal-mockup-wrap">
            <div className="achal-mockup">
              <div className="achal-mockup-screen">
                <div className="achal-mockup-hdr">
                  <span className="achal-mockup-time">9:41</span>
                  <div className="achal-mockup-bat" />
                </div>
                <div className="achal-mockup-body">
                  <div className="achal-mockup-emoji">✨</div>
                  <div className="achal-mockup-label">Support Portal</div>
                  <div className="achal-mockup-cam"><FiCamera size={28} /></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Login;